import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingBagIcon } from 'lucide-react';
import { CHECKOUT_URL, trackCheckout } from '../utils/checkout';
type ButtonVariant = 'primary' | 'rose' | 'secondary';
type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  children: React.ReactNode;
  className?: string;
  href?: string;
  target?: string;
  rel?: string;
  onLinkClick?: () => void;
  showIcon?: boolean;
  variant?: ButtonVariant;
};
const variantClasses: Record<ButtonVariant, string> = {
  primary: 'border-primary-strong bg-primary-strong text-primary-foreground shadow-elevated hover:bg-primary hover:border-primary focus-visible:ring-primary focus-visible:ring-offset-background',
  rose: 'border-primary bg-primary text-primary-foreground shadow-elevated hover:bg-primary-strong hover:border-primary-strong focus-visible:ring-accent-soft focus-visible:ring-offset-inverse',
  secondary: 'border-primary bg-surface text-primary-strong hover:bg-surface-subtle focus-visible:ring-primary focus-visible:ring-offset-background'
};
const iconClasses: Record<ButtonVariant, string> = {
  primary: 'text-accent-soft',
  rose: 'text-primary-foreground',
  secondary: 'text-primary'
};
export function Button({
  children,
  className = '',
  href,
  target,
  rel,
  onLinkClick,
  showIcon = true,
  variant = 'primary',
  ...props
}: ButtonProps) {
  const baseClasses = `inline-flex items-center justify-center gap-2.5 rounded-xl border px-6 py-3.5 text-center text-sm font-bold tracking-[0.04em] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 sm:px-8 sm:text-base ${variantClasses[variant]}`;
  const content = <>
      {showIcon && <ShoppingBagIcon size={18} strokeWidth={2} aria-hidden="true" className={`shrink-0 ${iconClasses[variant]}`} data-id="element-49" data-mp="3libot1794uhz" />}
      {children}
    </>;
  if (href) {
    return <motion.a href={href} target={target} rel={rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined)} onClick={onLinkClick} className={`${baseClasses} ${className}`} whileHover={{
      y: -2
    }} whileTap={{
      scale: 0.98
    }} data-id="element-50" data-mp="1jp45lgoipsqt">
        {content}
      </motion.a>;
  }
  return <motion.button className={`${baseClasses} ${className}`} whileHover={{
    y: -2
  }} whileTap={{
    scale: 0.98
  }} {...props} data-id="element-51" data-mp="13d0qsnrac4h1">
      {content}
    </motion.button>;
}
export function CheckoutButton({
  children,
  className = '',
  variant = 'primary'
}: {
  children: React.ReactNode;
  className?: string;
  variant?: ButtonVariant;
}) {
  return <Button href={CHECKOUT_URL} target="_blank" onLinkClick={trackCheckout} className={className} variant={variant} data-id="element-52" data-mp="1ma1w69or46li">
      {children}
    </Button>;
}